import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Data, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TreoNavigationItem, TreoNavigationService } from 'tsui/@treo';
import { ProjectSheetsService } from 'app/modules/projects/details/sheets/sheets.service';
import { ProjectSheet } from 'app/modules/projects/details/sheets/sheets.types';
import { AddProjectSheetComponent } from 'app/modules/projects/details/sheets/add-project-sheet/add-project-sheet.component';

@Component({
  selector: 'project-sheets-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectSheetsSidebarComponent {
  projectSheets: ProjectSheet[];
  menuData: TreoNavigationItem[];
  _projectId: string;

  // Private
  private _taskListsMenuData: TreoNavigationItem[];
  private _otherMenuData: TreoNavigationItem[];
  private _unsubscribeAll: Subject<void>;

  /**
   * Constructor
   *
   * @param {TreoNavigationService} _treoNavigationService
   * @param {ProjectsService} _projectsService
   * @param {MatDialog} _matDialog
   */
  constructor(
    private _treoNavigationService: TreoNavigationService,
    private _projectSheetsService: ProjectSheetsService,
    private _matDialog: MatDialog,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
  ) {
    // Set the private defaults
    this._taskListsMenuData = [];
    this._otherMenuData = [];
    this._unsubscribeAll = new Subject();

    // Set the defaults
    this.menuData = [];
  }

  ngOnInit(): void {
    this._projectId = this._projectSheetsService.projectId;

    // Filters
    this._projectSheetsService.projectSheets$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((projectSheets: ProjectSheet[]) => {
        this.projectSheets = projectSheets;
        this._generateTaskListsMenuLinks();
      });
    // Generate other menu links
    this._generateOtherMenuLinks();

    this._checkRedirectToFirstPipeline(this._activatedRoute);

    this._router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        //default route if navigate
        this._checkRedirectToFirstPipeline(this._activatedRoute);
      }
    });
  }

  /**
   * Check if we should redirect to first pipeline
   */
  _checkRedirectToFirstPipeline(route): void {
    if (!route.snapshot.firstChild && this.projectSheets.length > 0) {
      this._router.navigate(['./', this.projectSheets[0].id], { relativeTo: route });
    }
  }

  /**
   * Generate menus for folders
   *
   * @private
   */
  private _generateTaskListsMenuLinks(): void {
    // Reset the folders menu data
    this._taskListsMenuData = [];

    // Iterate through the folders
    this.projectSheets.forEach((projectSheet) => {
      // Generate menu item for the folder
      const menuItem: TreoNavigationItem = {
        id: projectSheet.id.toString(),
        title: projectSheet.name,
        icon: 'list_alt',
        type: 'basic',
        link: './' + projectSheet.id,
      };

      // Push the menu item to the folders menu data
      this._taskListsMenuData.push(menuItem);
    });

    const menuItem: TreoNavigationItem = {
      title: 'Add Sheet',
      type: 'basic',
      icon: 'add_circle_outline',
      function: (item) => {
        this.openSheetTemplateDialog();
      },
    };
    this._taskListsMenuData.push(menuItem);

    // Update the menu data
    this._updateMenuData();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  openSheetTemplateDialog(): void {
    //show modal
    const dialogRef = this._matDialog.open(AddProjectSheetComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Generate other menus
   *
   * @private
   */
  private _generateOtherMenuLinks(): void {
    // Settings menu
    this._otherMenuData.push({
      title: 'Templates',
      type: 'basic',
      icon: 'settings',
      link: '/settings/sheets',
    });

    // Update the menu data
    this._updateMenuData();
  }

  /**
   * Update the menu data
   *
   * @private
   */
  private _updateMenuData(): void {
    this.menuData = [
      {
        title: 'SHEETS',
        type: 'group',
        children: [...this._taskListsMenuData],
      },
      {
        type: 'divider',
      },
      ...this._otherMenuData,
    ];
  }
}
